import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailWarning, Loader2, RotateCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dataFormatata = (iso) => new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// Tab „Email-uri eșuate": emailuri (verificare/resetare/reamintiri) care nu
// au putut fi trimise prin Resend — retrimitere manuală sau ignorare.
export function FailedEmailsPanel() {
  const [entries, setEntries] = useState(null);
  const [eroare, setEroare] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios
      .get(`${API}/admin/failed-emails`, { withCredentials: true })
      .then((r) => setEntries(r.data.entries.filter((e) => !e.resolved)))
      .catch(() => setEroare("Nu am putut încărca lista."));
  };

  useEffect(load, []);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/failed-emails/${id}/retry`, {}, { withCredentials: true });
      toast.success("Email retrimis cu succes");
      setEntries((e) => e.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retrimiterea a eșuat din nou");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/failed-emails/${id}`, { withCredentials: true });
      setEntries((e) => e.filter((x) => x.id !== id));
    } catch {
      toast.error("Acțiunea a eșuat");
    } finally {
      setBusyId(null);
    }
  };

  if (eroare) return <p className="text-center text-rose-500 py-10">{eroare}</p>;
  if (!entries) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto mt-10" />;

  return (
    <div className="space-y-2" data-testid="failed-emails-panel">
      {entries.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-8">Niciun email eșuat momentan. 🎉</p>
      ) : (
        entries.map((e) => (
          <div key={e.id} data-testid={`failed-email-row-${e.id}`} className="rounded-2xl bg-white border border-rose-100 shadow-sm p-3.5">
            <div className="flex items-start gap-2.5">
              <MailWarning className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-slate-900 truncate">{e.subject}</p>
                <p className="text-xs text-slate-500 truncate">către {e.to_email}</p>
                <p className="text-[11px] text-rose-400 mt-0.5 truncate">{e.error}</p>
                <p className="text-[11px] text-slate-300 mt-0.5">{dataFormatata(e.created_at)}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  data-testid={`retry-failed-email-${e.id}`}
                  onClick={() => retry(e.id)}
                  disabled={busyId === e.id}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
                  title="Retrimite"
                >
                  {busyId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                </button>
                <button
                  data-testid={`dismiss-failed-email-${e.id}`}
                  onClick={() => dismiss(e.id)}
                  disabled={busyId === e.id}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 transition-colors disabled:opacity-40"
                  title="Ignoră"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
